import { api } from './client'
import type { MarketSegment } from './nomenclatoare'

// ---------- tipuri — oglindesc DTO-urile din Cipral.Application/Comercial/OferteModels.cs ----------

export type OfferStatus = 'Draft' | 'PendingApproval' | 'Approved' | 'Sent' | 'Expired' | 'Converted' | 'Rejected'

export interface OfferLine {
  id: string | null
  lineNumber: number
  articleId: string | null
  articleCode: string | null
  articleDescription: string | null
  customDescription: string | null
  requiresNewMold: boolean
  quantityThousands: number
  pricePer1000: number
  discountPercent: number
  vatRateId: string | null
  vatRatePercent: number | null
  pricingSourceLabel: string | null
  lineValue: number
}

export type SaveOfferLinePayload = Pick<
  OfferLine,
  'articleId' | 'customDescription' | 'requiresNewMold' | 'quantityThousands' | 'pricePer1000' | 'discountPercent' | 'vatRateId'
>

export interface OfferListItem {
  id: string
  offerNumber: string
  versionNumber: number
  offerDate: string
  clientId: string
  clientName: string
  agentUserName: string
  status: OfferStatus
  validUntil: string
  isExpired: boolean
  isExpiringSoon: boolean
  total: number
}

export interface OfferDetail {
  id: string
  offerNumber: string
  versionNumber: number
  isCurrentVersion: boolean
  previousVersionId: string | null
  offerDate: string
  clientId: string
  clientName: string
  contactId: string | null
  contactName: string | null
  agentUserId: string
  agentUserName: string
  segment: MarketSegment | null
  validityDays: number
  validUntil: string
  isExpired: boolean
  isExpiringSoon: boolean
  paymentTerms: string | null
  estimatedDeliveryTerm: string | null
  estimatedDeliveryTermOverridden: boolean
  notes: string | null
  status: OfferStatus
  sourceClientRequestId: string | null
  convertedToOrderId: string | null
  lines: OfferLine[]
  total: number
  maxDiscountPercent: number
}

export interface SaveOfferPayload {
  offerDate: string
  clientId: string
  contactId: string | null
  agentUserId: string
  segment: MarketSegment | null
  validityDays: number
  paymentTerms: string | null
  estimatedDeliveryTerm: string | null
  estimatedDeliveryTermOverridden: boolean
  notes: string | null
  sourceClientRequestId: string | null
  lines: SaveOfferLinePayload[]
  belowCostOverrideReason: string | null
}

// ---------- FR-MDP-008 — blocare vânzare sub cost ----------

export interface BelowCostViolation { articleCode: string; pricePer1000: number; standardCost: number }
export interface BelowCostViolationsResponse { message: string; violations: BelowCostViolation[] }

// ---------- FR-OFR-009 — verificare stoc la ofertare ----------

export const getAvailableStock = (articleId: string) =>
  api.get<{ availableThousands: number }>(`/comercial/oferte/disponibil-stoc/${articleId}`).then((r) => r.data.availableThousands)

export interface ConvertOfferToOrderPayload {
  workPointId: string
  deliveryAddressId: string | null
  billingAddressId: string | null
  clientPoReference: string | null
  requestedDeliveryDate: string
}

export const listOffers = () => api.get<OfferListItem[]>('/comercial/oferte').then((r) => r.data)
export const getOffer = (id: string) => api.get<OfferDetail>(`/comercial/oferte/${id}`).then((r) => r.data)
export const getOfferHistory = (offerNumber: string) =>
  api.get<OfferListItem[]>(`/comercial/oferte/numar/${offerNumber}/istoric`).then((r) => r.data)
export const createOffer = (payload: SaveOfferPayload) =>
  api.post<{ id: string }>('/comercial/oferte', payload).then((r) => r.data)
export const updateOffer = (id: string, payload: SaveOfferPayload) => api.put(`/comercial/oferte/${id}`, payload)
export const createOfferRevision = (id: string) =>
  api.post<{ id: string }>(`/comercial/oferte/${id}/revizie`).then((r) => r.data)
export const submitOfferForApproval = (id: string) =>
  api.post<{ pendingApproval: boolean }>(`/comercial/oferte/${id}/submit-aprobare`).then((r) => r.data)
export const sendOffer = (id: string) => api.post(`/comercial/oferte/${id}/trimite`)
export const getOfferDocumentHtml = (id: string) =>
  api.get<string>(`/comercial/oferte/${id}/document`, { responseType: 'text' }).then((r) => r.data)
export const convertOfferToOrder = (id: string, payload: ConvertOfferToOrderPayload) =>
  api.post<{ id: string }>(`/comercial/oferte/${id}/converteste-in-comanda`, payload).then((r) => r.data)
